import React, { useState, useEffect } from "react";
import DOMPurify from "dompurify";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useApi, BACKEND } from "@/lib/api";
import { MediaPicker, MediaImg } from "@/components/MediaPicker";
import { PageHeader, Panel, PageLoader, StatusBadge, EmptyState, Btn, TextInput, TextArea } from "@/lib/ui";
import {
  Newspaper, Plus, Trash2, Eye, ImageIcon, UploadCloud, History, RotateCcw,
  Star, Search as SearchIcon, DownloadCloud, Globe, PencilLine,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const STATUSES = ["draft", "scheduled", "published", "archived"];
const TONE = { draft: "muted", scheduled: "warn", published: "ok", archived: "err" };
const fmt = (v) => (v ? new Date(v).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—");
const toLocalInput = (iso) => { if (!iso) return ""; const d = new Date(iso); const o = d.getTimezoneOffset(); return new Date(d.getTime() - o * 60000).toISOString().slice(0, 16); };

const EMPTY = {
  title: "", slug: "", excerpt: "", content: "", cover_image: "", category: "General",
  author: "Gopi Gadhvi Group", tags: [], status: "draft", featured: false, scheduled_at: null,
  seo_title: "", seo_description: "", canonical_url: "", og_title: "", og_description: "", og_image: "",
};

function Preview({ form }) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Btn variant="outline" size="sm" type="button" data-testid="blog-preview-open"><Eye className="h-4 w-4" /> Preview</Btn>
      </DialogTrigger>
      <DialogContent className="bg-surface border-border max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="sr-only">Preview</DialogTitle><DialogDescription className="sr-only">Post preview as it will appear on the website</DialogDescription></DialogHeader>
        <article className="space-y-4" data-testid="blog-preview-body">
          {form.cover_image && <MediaImg src={form.cover_image} alt="" className="w-full rounded-md border border-border object-cover max-h-72" />}
          <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-primary">{form.category} · {form.author}</div>
          <h1 className="font-head text-3xl font-bold tracking-tight">{form.title || "Untitled post"}</h1>
          {form.excerpt && <p className="text-muted-foreground">{form.excerpt}</p>}
          <div className="prose prose-invert prose-sm max-w-none text-foreground/90 whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(form.content || "<em>No content yet.</em>") }} />
        </article>
      </DialogContent>
    </Dialog>
  );
}

function BlogEditor({ post, onClose }) {
  const api = useApi();
  const qc = useQueryClient();
  const isNew = !post?.id;
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (post?.id) { setLoading(true); api.get(`/cms/blog/${post.id}`).then((d) => setForm({ ...EMPTY, ...d, tags: d.tags || [] })).finally(() => setLoading(false)); }
    else setForm(EMPTY);
  }, [post?.id]); // eslint-disable-line

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const inval = () => { qc.invalidateQueries({ queryKey: ["blog"] }); qc.invalidateQueries({ queryKey: ["blog-stats"] }); };

  const EDITABLE = ["title", "slug", "excerpt", "content", "cover_image", "category", "author", "tags", "status", "featured", "scheduled_at", "seo_title", "seo_description", "canonical_url", "og_title", "og_description", "og_image"];
  const payload = () => {
    const p = {};
    EDITABLE.forEach((k) => { p[k] = form[k]; });
    p.tags = Array.isArray(form.tags) ? form.tags : String(form.tags).split(",").map((t) => t.trim()).filter(Boolean);
    if (p.status !== "scheduled") p.scheduled_at = null;
    return p;
  };

  const save = useMutation({
    mutationFn: () => {
      const p = payload();
      if (p.status === "scheduled" && !p.scheduled_at) throw new Error("Pick a publish date/time for a scheduled post");
      return isNew ? api.post("/cms/blog", p) : api.patch(`/cms/blog/${post.id}`, p);
    },
    onSuccess: () => { toast.success(isNew ? "Post created" : "Post saved"); inval(); onClose(); },
    onError: (e) => toast.error(e.message),
  });

  const del = useMutation({
    mutationFn: () => api.del(`/cms/blog/${post.id}`),
    onSuccess: () => { toast.success("Post deleted"); inval(); onClose(); },
    onError: (e) => toast.error(e.message),
  });

  const rollback = useMutation({
    mutationFn: (v) => api.post(`/cms/blog/${post.id}/rollback/${v}`),
    onSuccess: (d) => { toast.success("Rolled back"); setForm({ ...EMPTY, ...d, tags: d.tags || [] }); inval(); },
    onError: (e) => toast.error(e.message),
  });

  return (
    <div className="space-y-5" data-testid="blog-editor">
      {loading ? <div className="py-20"><PageLoader /></div> : (<>
      <div className="flex items-center gap-2">
        <StatusBadge tone={TONE[form.status]} dot>{form.status}</StatusBadge>
        {form.featured && <StatusBadge tone="gold"><Star className="h-3 w-3" /> Featured</StatusBadge>}
        <div className="flex-1" />
        <Preview form={form} />
      </div>

      <Tabs defaultValue="content">
        <TabsList className="grid grid-cols-4 w-full">
          <TabsTrigger value="content" data-testid="blog-tab-content">Content</TabsTrigger>
          <TabsTrigger value="seo" data-testid="blog-tab-seo">SEO & Social</TabsTrigger>
          <TabsTrigger value="publish" data-testid="blog-tab-publish">Publish</TabsTrigger>
          <TabsTrigger value="history" data-testid="blog-tab-history" disabled={isNew} title="Version history" aria-label="Version history"><History className="h-3.5 w-3.5 mr-1" /> History</TabsTrigger>
        </TabsList>

        <TabsContent value="content" className="space-y-3 mt-4">
          <TextInput label="Title" value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="blog-title" placeholder="Post title" />
          <TextInput label="Slug (optional — auto from title)" value={form.slug || ""} onChange={(e) => set("slug", e.target.value)} data-testid="blog-slug" placeholder="my-post-slug" />
          <div className="grid grid-cols-2 gap-3">
            <TextInput label="Category" value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="blog-category" />
            <TextInput label="Author" value={form.author} onChange={(e) => set("author", e.target.value)} data-testid="blog-author" />
          </div>
          <TextArea label="Excerpt" rows={2} value={form.excerpt} onChange={(e) => set("excerpt", e.target.value)} data-testid="blog-excerpt" placeholder="Short summary shown in listings" />
          <div>
            <span className="text-xs text-muted-foreground mb-1.5 block">Cover image</span>
            <div className="flex items-center gap-2">
              <input className="w-full h-9 rounded-md border border-border bg-surface px-3 text-sm" placeholder="Image URL or pick from Media Library"
                value={form.cover_image} onChange={(e) => set("cover_image", e.target.value)} data-testid="blog-cover-url" />
              <MediaPicker onPick={(url) => set("cover_image", url)} />
            </div>
            {form.cover_image && <MediaImg src={form.cover_image} alt="" className="mt-2 h-28 rounded-md border border-border object-cover" testid="blog-cover-preview" />}
          </div>
          <TextArea label="Content (HTML supported)" rows={10} value={form.content} onChange={(e) => set("content", e.target.value)} data-testid="blog-content" className="font-mono text-xs" />
          <TextInput label="Tags (comma separated)" value={Array.isArray(form.tags) ? form.tags.join(", ") : form.tags}
            onChange={(e) => set("tags", e.target.value.split(",").map((t) => t.trim()).filter(Boolean))} data-testid="blog-tags" placeholder="luxury, real estate" />
        </TabsContent>

        <TabsContent value="seo" className="space-y-3 mt-4">
          <TextInput label="SEO title" value={form.seo_title || ""} onChange={(e) => set("seo_title", e.target.value)} data-testid="blog-seo-title" />
          <TextArea label="Meta description" rows={2} value={form.seo_description || ""} onChange={(e) => set("seo_description", e.target.value)} data-testid="blog-seo-desc" />
          <TextInput label="Canonical URL" value={form.canonical_url || ""} onChange={(e) => set("canonical_url", e.target.value)} data-testid="blog-canonical" placeholder="https://gopigadhvi.com/blog/…" />
          <div className="border-t border-border pt-3 space-y-3">
            <div className="text-[11px] uppercase tracking-[0.1em] text-muted-foreground">Social / Open Graph</div>
            <TextInput label="OG title" value={form.og_title || ""} onChange={(e) => set("og_title", e.target.value)} data-testid="blog-og-title" />
            <TextArea label="OG description" rows={2} value={form.og_description || ""} onChange={(e) => set("og_description", e.target.value)} data-testid="blog-og-desc" />
            <TextInput label="OG image URL" value={form.og_image || ""} onChange={(e) => set("og_image", e.target.value)} data-testid="blog-og-image" />
          </div>
        </TabsContent>

        <TabsContent value="publish" className="space-y-4 mt-4">
          <div>
            <span className="text-xs text-muted-foreground mb-1.5 block">Status</span>
            <Select value={form.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger data-testid="blog-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} data-testid={`blog-status-${s}`}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {form.status === "scheduled" && (
            <div>
              <span className="text-xs text-muted-foreground mb-1.5 block">Publish at</span>
              <input type="datetime-local" data-testid="blog-scheduled-at" value={toLocalInput(form.scheduled_at)}
                onChange={(e) => set("scheduled_at", e.target.value ? new Date(e.target.value).toISOString() : null)}
                className="w-full h-9 rounded-md border border-border bg-surface px-3 text-sm" />
              <p className="text-xs text-muted-foreground mt-1.5">The post auto-publishes at this time and appears on the public website.</p>
            </div>
          )}
          <div className="flex items-center justify-between border border-border rounded-md px-3 py-2.5">
            <div><div className="text-sm font-medium">Featured post</div><div className="text-xs text-muted-foreground">Highlight on the website homepage.</div></div>
            <Switch checked={form.featured} onCheckedChange={(v) => set("featured", v)} data-testid="blog-featured-switch" />
          </div>
          {!isNew && <div className="text-xs text-muted-foreground font-mono">Published: {fmt(form.published_at)}</div>}
        </TabsContent>

        <TabsContent value="history" className="space-y-2 mt-4" data-testid="blog-history">
          {(!form.versions || form.versions.length === 0) && <p className="text-xs text-muted-foreground py-4">No previous versions yet. Versions are saved each time you edit.</p>}
          {(form.versions || []).slice().reverse().map((v) => (
            <div key={v.version} className="flex items-center justify-between gap-2 border border-border/60 rounded-md p-2.5" data-testid={`blog-version-${v.version}`}>
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">v{v.version} · {v.title}</div>
                <div className="text-xs text-muted-foreground flex items-center gap-1.5 flex-wrap">
                  <StatusBadge tone={TONE[v.status]}>{v.status}</StatusBadge>
                  {v.by} · {fmt(v.at)} {v.note ? `· ${v.note}` : ""}
                </div>
              </div>
              <Btn variant="outline" size="sm" type="button" onClick={() => rollback.mutate(v.version)} disabled={rollback.isPending} data-testid={`blog-rollback-${v.version}`}>
                <RotateCcw className="h-3.5 w-3.5" /> Restore
              </Btn>
            </div>
          ))}
        </TabsContent>
      </Tabs>

      <div className="flex items-center gap-2 border-t border-border pt-4">
        <Btn onClick={() => save.mutate()} disabled={save.isPending || !form.title.trim()} data-testid="blog-save-btn">
          {save.isPending ? "Saving…" : isNew ? "Create post" : "Save changes"}
        </Btn>
        {!isNew && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Btn variant="danger" data-testid="blog-delete-btn"><Trash2 className="h-4 w-4" /> Delete</Btn>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-surface border-border">
              <AlertDialogHeader>
                <AlertDialogTitle>Delete this post?</AlertDialogTitle>
                <AlertDialogDescription>This permanently removes “{form.title}”. This action cannot be undone.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="blog-delete-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={() => del.mutate()} data-testid="blog-delete-confirm" className="bg-err text-white hover:bg-err/90">Delete</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
      </>)}
    </div>
  );
}

function ImportButton() {
  const api = useApi();
  const qc = useQueryClient();
  const imp = useMutation({
    mutationFn: () => api.post("/cms/blog/import"),
    onSuccess: (d) => { toast.success(`Imported ${d.imported} post(s) as drafts · ${d.skipped} skipped`); qc.invalidateQueries({ queryKey: ["blog"] }); qc.invalidateQueries({ queryKey: ["blog-stats"] }); },
    onError: (e) => toast.error(e.message),
  });
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Btn variant="outline" size="sm" data-testid="blog-import-btn"><DownloadCloud className="h-4 w-4" /> Import from live site</Btn>
      </AlertDialogTrigger>
      <AlertDialogContent className="bg-surface border-border">
        <AlertDialogHeader>
          <AlertDialogTitle>Import posts from the live website?</AlertDialogTitle>
          <AlertDialogDescription>Existing posts on gopigadhvi.com are copied into the Command Centre as <strong>drafts</strong> for review. Nothing is overwritten, and the live website is not changed.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="blog-import-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={() => imp.mutate()} disabled={imp.isPending} data-testid="blog-import-confirm">{imp.isPending ? "Importing…" : "Import as drafts"}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default function Blog() {
  const api = useApi();
  const qc = useQueryClient();
  const [filter, setFilter] = useState("all");
  const [editing, setEditing] = useState(null); // {} for new, post for edit, null closed
  const [search, setSearch] = useState("");

  const { data: stats } = useQuery({ queryKey: ["blog-stats"], queryFn: () => api.get("/cms/blog/stats") });
  const { data: posts = [], isLoading } = useQuery({ queryKey: ["blog", filter], queryFn: () => api.get(`/cms/blog${filter === "all" ? "" : `?status=${filter}`}`) });

  const quick = useMutation({
    mutationFn: ({ id, status }) => api.patch(`/cms/blog/${id}`, { status }),
    onSuccess: () => { toast.success("Status updated"); qc.invalidateQueries({ queryKey: ["blog"] }); qc.invalidateQueries({ queryKey: ["blog-stats"] }); },
    onError: (e) => toast.error(e.message),
  });

  const shown = posts.filter((p) => !search || p.title.toLowerCase().includes(search.toLowerCase()));

  return (
    <div data-testid="blog-page">
      <PageHeader kicker="Content · Website CMS" title="Blog">
        <ImportButton />
        <Btn size="sm" onClick={() => setEditing({})} data-testid="blog-new-btn"><Plus className="h-4 w-4" /> New post</Btn>
      </PageHeader>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        {["all", ...STATUSES].map((s) => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`blog-filter-${s}`}
            className={cn("text-xs rounded-full px-3 py-1.5 border transition-colors capitalize",
              filter === s ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground hover:text-foreground")}>
            {s}{stats ? ` · ${s === "all" ? (stats.total ?? 0) : (stats[s] ?? 0)}` : ""}
          </button>
        ))}
        <div className="flex-1" />
        <div className="relative">
          <SearchIcon className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search titles…" data-testid="blog-search"
            className="h-9 w-48 rounded-md border border-border bg-surface pl-8 pr-3 text-sm" />
        </div>
      </div>

      {isLoading ? <PageLoader /> : shown.length === 0 ? (
        <Panel className="p-10"><EmptyState icon={Newspaper} title="No posts yet"
          hint="Create your first post, or import existing posts from the live website as drafts."
          action={<Btn size="sm" className="mt-2" onClick={() => setEditing({})} data-testid="blog-empty-new"><Plus className="h-4 w-4" /> New post</Btn>} /></Panel>
      ) : (
        <Panel className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="blog-table">
              <thead>
                <tr className="border-b border-border text-left text-[11px] uppercase tracking-[0.1em] text-muted-foreground">
                  <th className="px-4 py-3 font-medium">Title</th>
                  <th className="px-4 py-3 font-medium">Category</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium whitespace-nowrap">Updated</th>
                  <th className="px-4 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((p) => (
                  <tr key={p.id} data-testid={`blog-row-${p.id}`} className="border-b border-border/50 hover:bg-accent/40 transition-colors">
                    <td className="px-4 py-3 font-medium max-w-sm">
                      <button onClick={() => setEditing(p)} className="text-left hover:text-primary flex items-center gap-2" data-testid={`blog-edit-${p.id}`}>
                        {p.featured && <Star className="h-3.5 w-3.5 text-primary shrink-0" />}
                        <span className="truncate">{p.title}</span>
                      </button>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{p.category}</td>
                    <td className="px-4 py-3"><StatusBadge tone={TONE[p.status]} dot>{p.status}</StatusBadge></td>
                    <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{fmt(p.updated_at)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1.5">
                        {p.status !== "published" ? (
                          <Btn variant="ghost" size="sm" onClick={() => quick.mutate({ id: p.id, status: "published" })} data-testid={`blog-publish-${p.id}`}><Globe className="h-3.5 w-3.5" /> Publish</Btn>
                        ) : (
                          <Btn variant="ghost" size="sm" onClick={() => quick.mutate({ id: p.id, status: "draft" })} data-testid={`blog-unpublish-${p.id}`}>Unpublish</Btn>
                        )}
                        <Btn variant="ghost" size="icon" onClick={() => setEditing(p)} data-testid={`blog-open-${p.id}`}><PencilLine className="h-4 w-4" /></Btn>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}

      <Sheet open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto bg-surface border-border">
          <SheetHeader><SheetTitle className="text-left">{editing?.id ? "Edit post" : "New post"}</SheetTitle><SheetDescription className="text-left">{editing?.id ? "Update this blog post, its SEO and publishing settings." : "Create a new blog post for the website."}</SheetDescription></SheetHeader>
          {editing && <div className="mt-4"><BlogEditor post={editing} onClose={() => setEditing(null)} /></div>}
        </SheetContent>
      </Sheet>
    </div>
  );
}
